import { ethers } from 'ethers';

export interface TokenSwap {
  hash: string;
  timestamp: number;
  type: 'buy' | 'sell';
  amount: string;
  price: string;
  value: string;
  dex: string;
  wallet: string;
}

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  price: string;
  marketCap: string;
  volume24h: string;
  priceChange24h: string;
}

export interface TrackingConfig {
  tokenAddress: string;
  walletAddress: string;
  minAmount: string;
  enabled: boolean;
}

class TokenTrackingService {
  private trackedTokens: Map<string, TrackingConfig> = new Map();
  private swapHistory: TokenSwap[] = [];
  private isTracking: boolean = false;
  private provider: ethers.providers.Provider | null = null;

  constructor() {
    this.loadDefaultTrackings();
  }

  private async initializeProvider() {
    try {
      if (typeof window !== 'undefined' && window.ethereum) {
        this.provider = new ethers.providers.Web3Provider(window.ethereum);
      } else {
        this.provider = new ethers.providers.JsonRpcProvider('https://eth.llamarpc.com');
      }
    } catch (error) {
      console.error('Failed to initialize token tracking provider:', error);
    }
  }

  private loadDefaultTrackings() {
    // Add the default tracking addresses
    this.addTracking({
      tokenAddress: '0x4675c7e5baafbffbca748158becba61ef3b0a263',
      walletAddress: '0xc8e042333e09666a627e913b0c14053d0ffef17e',
      minAmount: '1000', // $1000 minimum
      enabled: true
    });
  }

  // Add a new token to track
  addTracking(config: TrackingConfig) {
    const key = `${config.tokenAddress}-${config.walletAddress}`;
    this.trackedTokens.set(key, config);
    this.startTracking();
  }

  // Remove tracking for a token
  removeTracking(tokenAddress: string, walletAddress: string) {
    const key = `${tokenAddress}-${walletAddress}`;
    this.trackedTokens.delete(key);
    
    if (this.trackedTokens.size === 0) {
      this.stopTracking();
    }
  }

  // Get all tracked tokens
  getTrackedTokens(): TrackingConfig[] {
    return Array.from(this.trackedTokens.values());
  }

  // Start tracking swaps
  private startTracking() {
    if (this.isTracking || !this.provider) return;
    
    this.isTracking = true;
    this.monitorSwaps();
  }

  // Stop tracking swaps
  private stopTracking() {
    this.isTracking = false;
  }

  // Monitor swaps for tracked tokens
  private async monitorSwaps() {
    if (!this.isTracking || !this.provider) return;

    try {
      for (const [key, config] of this.trackedTokens) {
        if (!config.enabled) continue;

        await this.checkTokenSwaps(config);
      }
    } catch (error) {
      console.error('Error monitoring swaps:', error);
    }

    // Continue monitoring every 30 seconds
    if (this.isTracking) {
      setTimeout(() => this.monitorSwaps(), 30000);
    }
  }

  // Check for token swaps
  private async checkTokenSwaps(config: TrackingConfig) {
    try {
      // Get recent transactions for the wallet
      const blockNumber = await this.provider!.getBlockNumber();
      const fromBlock = Math.max(0, blockNumber - 1000); // Last ~1000 blocks

      // Filter for token transfers
      const filter = {
        fromBlock,
        toBlock: 'latest',
        address: config.tokenAddress,
        topics: [
          ethers.utils.id('Transfer(address,address,uint256)'),
          null,
          ethers.utils.hexZeroPad(config.walletAddress, 32) // To address
        ]
      };

      const logs = await this.provider!.getLogs(filter);
      
      for (const log of logs) {
        await this.processTokenTransfer(log, config);
      }
    } catch (error) {
      console.error('Error checking token swaps:', error);
    }
  }

  // Process a token transfer
  private async processTokenTransfer(log: any, config: TrackingConfig) {
    try {
      const from = ethers.utils.getAddress('0x' + log.topics[1].slice(26));
      const to = ethers.utils.getAddress('0x' + log.topics[2].slice(26));
      const amount = ethers.BigNumber.from(log.data);

      // Determine if it's a buy or sell
      const isBuy = to.toLowerCase() === config.walletAddress.toLowerCase();
      const isSell = from.toLowerCase() === config.walletAddress.toLowerCase();

      if (!isBuy && !isSell) return;

      // Get transaction details
      const tx = await this.provider!.getTransaction(log.transactionHash);
      const receipt = await this.provider!.getTransactionReceipt(log.transactionHash);

      // Calculate USD value (simplified)
      const tokenInfo = await this.getTokenInfo(config.tokenAddress);
      const value = this.calculateUSDValue(amount, tokenInfo);

      // Check if it meets minimum amount threshold
      if (parseFloat(value) < parseFloat(config.minAmount)) return;

      const swap: TokenSwap = {
        hash: log.transactionHash,
        timestamp: (await this.provider!.getBlock(log.blockNumber)).timestamp,
        type: isBuy ? 'buy' : 'sell',
        amount: ethers.utils.formatUnits(amount, tokenInfo.decimals),
        price: tokenInfo.price,
        value: value,
        dex: this.detectDEX(receipt),
        wallet: config.walletAddress
      };

      this.addSwapToHistory(swap);
      this.notifySwap(swap, config);

    } catch (error) {
      console.error('Error processing token transfer:', error);
    }
  }

  // Get token information
  private async getTokenInfo(address: string): Promise<TokenInfo> {
    // This would typically call a token API or contract
    // For demo purposes, return mock data
    return {
      address,
      symbol: 'TOKEN',
      name: 'Tracked Token',
      decimals: 18,
      price: '0.05',
      marketCap: '1000000',
      volume24h: '50000',
      priceChange24h: '+5.2'
    };
  }

  // Calculate USD value
  private calculateUSDValue(amount: ethers.BigNumberish, tokenInfo: TokenInfo): string {
    const tokenAmount = parseFloat(ethers.utils.formatUnits(amount, tokenInfo.decimals));
    const price = parseFloat(tokenInfo.price);
    return (tokenAmount * price).toFixed(2);
  }

  // Detect which DEX was used
  private detectDEX(receipt: any): string {
    // Check for known DEX contracts
    const dexContracts: { [key: string]: string } = {
      '0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D': 'Uniswap V2',
      '0xE592427A0AEce92De3Edee1F18E0157C05861564': 'Uniswap V3',
      '0x1111111254EEB25477B68fb85Ed929f73A960582': '1inch',
      '0x881D40237659C251811CEC9c364ef91dC08D300C': 'Metamask Swap'
    };

    for (const log of receipt.logs) {
      if (dexContracts[log.address]) {
        return dexContracts[log.address];
      }
    }

    return 'Unknown DEX';
  }

  // Add swap to history
  private addSwapToHistory(swap: TokenSwap) {
    this.swapHistory.unshift(swap);
    
    // Keep only last 100 swaps
    if (this.swapHistory.length > 100) {
      this.swapHistory = this.swapHistory.slice(0, 100);
    }
  }

  // Notify about new swap
  private notifySwap(swap: TokenSwap, config: TrackingConfig) {
    // Emit event or call callback
    console.log('New swap detected:', swap);
    
    // You can add event listeners here
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tokenSwap', { detail: swap }));
    }
  }

  // Get swap history
  getSwapHistory(): TokenSwap[] {
    return this.swapHistory;
  }

  // Get swaps for specific token
  getTokenSwaps(tokenAddress: string): TokenSwap[] {
    return this.swapHistory.filter(swap => 
      this.trackedTokens.has(`${tokenAddress}-${swap.wallet}`)
    );
  }

  // Get recent swaps (last 24 hours)
  getRecentSwaps(hours: number = 24): TokenSwap[] {
    const cutoff = Date.now() - (hours * 60 * 60 * 1000);
    return this.swapHistory.filter(swap => swap.timestamp * 1000 > cutoff);
  }

  // Get tracking statistics
  getTrackingStats() {
    const recentSwaps = this.getRecentSwaps(24);
    const buySwaps = recentSwaps.filter(swap => swap.type === 'buy');
    const sellSwaps = recentSwaps.filter(swap => swap.type === 'sell');

    const totalBuyVolume = buySwaps.reduce((sum, swap) => sum + parseFloat(swap.value), 0);
    const totalSellVolume = sellSwaps.reduce((sum, swap) => sum + parseFloat(swap.value), 0);

    return {
      totalSwaps: recentSwaps.length,
      buySwaps: buySwaps.length,
      sellSwaps: sellSwaps.length,
      totalBuyVolume: totalBuyVolume.toFixed(2),
      totalSellVolume: totalSellVolume.toFixed(2),
      netFlow: (totalBuyVolume - totalSellVolume).toFixed(2),
      trackedTokens: this.trackedTokens.size
    };
  }

  // Format time
  formatTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleString();
  }

  // Check if tracking is active
  isTrackingActive(): boolean {
    return this.isTracking;
  }
}

export const tokenTrackingService = new TokenTrackingService();
